import { Text } from '../Text/Text';
import { get, orderBy } from 'lodash';
import moment from 'moment-timezone';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { isToday } from '../Utils';
import {
  AssignmentGroupBy,
  AssignmentType,
  AssignmentsListItemProps,
  AssignmentsListProps,
} from './Assignments.interface';
import './AssignmentsList.module.css';
import { AssigmnentsListItem } from './AssignmentsListItem';

const isInPast = (item: AssignmentsListItemProps): boolean => {
  const startOfToday = moment().set({ hour: 0, minute: 0, seconds: 0 }).unix();
  const due = moment(item.dueDate).unix();

  return startOfToday > due;
};

export const AssignmentsList: React.FC<AssignmentsListProps> = ({
  items,
  groupBy,
  readonly,
}) => {
  const { t } = useTranslation();

  const groupItemsByDay = (
    items: AssignmentsListItemProps[],
  ): { [key: string]: AssignmentsListItemProps[] } => {
    const output: { [key: string]: AssignmentsListItemProps[] } = {};
    const sorted = orderBy(items, a => moment(a.dueDate), 'desc');

    for (const item of sorted) {
      const dueDate = moment(item.dueDate);
      let index = dueDate.format('LL');

      if (item.type === AssignmentType.Assigned && isInPast(item)) {
        index = t('overdue', { defaultValue: 'Überfällig' });
      } else {
        index = isToday(dueDate.toDate())
          ? t('today', { defaultValue: 'Heute' })
          : dueDate.format('LL');
      }

      if (get(output, index, false) === false) {
        output[index] = [];
      }

      output[index].push(item);
    }

    // Sortieren der Schlüssel (Datumsstrings)
    const sortedKeys = Object.keys(output).sort(sortByDate);

    // Erstellen eines neuen Objekts mit sortierten Schlüsseln
    const sortedOutput: { [key: string]: AssignmentsListItemProps[] } = {};
    for (const key of sortedKeys) {
      sortedOutput[key] = output[key];
    }

    return sortedOutput;
  };

  const groupItemsByCourse = (
    items: AssignmentsListItemProps[],
  ): { [key: string]: AssignmentsListItemProps[] } => {
    const unordered: { [key: string]: AssignmentsListItemProps[] } = {};

    for (const item of items) {
      const courseName = item.courseName;

      if (get(unordered, courseName, false) === false) {
        unordered[courseName] = [];
      }

      unordered[courseName].push(item);
    }

    // Sort alphabetically
    const ordered = Object.keys(unordered)
      .sort()
      .reverse()
      .reduce((obj, key) => {
        obj[key] = unordered[key];
        return obj;
      }, {} as { [key: string]: AssignmentsListItemProps[] });

    return ordered;
  };

  if (groupBy === AssignmentGroupBy.None) {
    return (
      <ul className="assignmentsList">
        {items.map((item, index) => (
          <AssigmnentsListItem key={index} {...item} />
        ))}
      </ul>
    );
  }

  const grouped =
    groupBy === AssignmentGroupBy.Day
      ? groupItemsByDay(items)
      : groupItemsByCourse(items);

  return (
    <ul className="assignmentsList">
      {Object.keys(grouped).map((key, index) => (
        <React.Fragment key={index}>
          <li className="divider">
            <Text weight="bold">{`${key} (${grouped[key].length})`}</Text>
          </li>
          {grouped[key].map((item, index) => (
            <AssigmnentsListItem key={index} readOnly={readonly} {...item} />
          ))}
        </React.Fragment>
      ))}
    </ul>
  );
};

export const sortByDate = (a: string, b: string): number => {
  //keine gültiges Datum ursprünglichen Reihenfolge
  const dateA: Date | null = moment(a, true).isValid()
    ? moment(a).toDate()
    : null;
  const dateB: Date | null = moment(b, true).isValid()
    ? moment(b).toDate()
    : null;

  if (dateA && dateB) {
    return dateA.getTime() - dateB.getTime();
  } else if (dateA) {
    return -1;
  } else if (dateB) {
    return 1;
  } else {
    // kein gültiges Datum = ursprünglichen Reihenfolge
    return 0;
  }
};
